import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { authUrl } from '@/constants/environment';
import { HttpService } from '@/services';
import type {
  Announcement,
  CurrentUser,
  ReportEntityType,
  TokenPayload,
  UserCourseStatus,
} from '@/types';
import { isDevelopment } from '@/utils/server/system';

export default class UserService {
  public static async getMe(): Promise<CurrentUser> {
    const response = await HttpService.get<CurrentUser>('/users/me');
    return response.data.data;
  }

  public static async updateMe(userData: Partial<CurrentUser>): Promise<CurrentUser> {
    const response = await HttpService.patch<CurrentUser>('/users/me', userData);
    return response.data.data;
  }

  public static async getMyCourseStatus(courseId: string): Promise<UserCourseStatus> {
    const response = await HttpService.get<UserCourseStatus>(
      `/users/me/courses/${courseId}/status`,
    );
    return response.data.data;
  }

  public static async updatePlannedFinishDate(
    courseId: string,
    plannedToFinishAt: string,
  ): Promise<{ plannedToFinishAt: string }> {
    const response = await HttpService.patch<{ plannedToFinishAt: string }>(
      `/users/me/courses/${courseId}/status/planned-finish-date`,
      {
        plannedToFinishAt,
      },
    );
    return response.data.data;
  }

  public static async getAccessToken(): Promise<string | null> {
    const response = await HttpService.get<{ accessToken: string | null }>(authUrl, undefined, {
      withCredentials: true,
    });
    return response.data.data.accessToken;
  }

  public static isAccessTokenValid(token: string): boolean {
    const payload = jwtDecode<TokenPayload>(token);
    const expiresAt = dayjs.unix(payload.exp);
    const isNotExpired = dayjs().isBefore(expiresAt);
    if (isDevelopment) {
      console.debug(`Session: ${isNotExpired ? 'valid' : 'invalid'}; expires: ${expiresAt}`);
    }
    return isNotExpired;
  }

  public static async reportContent(
    id: string,
    entityType: ReportEntityType,
    report: string,
  ): Promise<boolean> {
    const response = await HttpService.post(`/users/me/report/${id}`, {
      entityType,
      report,
    });
    return response.data.success;
  }

  public static async getMyAnnouncements(): Promise<Announcement[]> {
    const response = await HttpService.get<Announcement[]>('/users/me/announcements');
    return response.data.data;
  }

  public static async getAnnouncements(): Promise<Announcement[]> {
    const response = await HttpService.get<Announcement[]>('/announcements');
    return response.data.data;
  }

  public static async markAnnouncementsAsViewed(): Promise<CurrentUser> {
    const response = await HttpService.patch<CurrentUser>('/users/me', {
      announcementsViewedAt: new Date().toISOString(),
    });
    return response.data.data;
  }
}
